/**
 * A seção "Relatório Fotográfico" da ficha da visita (ADR-046; spec relatorio-fotografico-e-planilhas-de-controle,
 * Req 2/4.3, design §6): o RF ligado à escola da visita pelo INEP — a capa (data, coordenada, de onde veio o INEP) e as
 * fotos (aba, número, legenda), cada uma aberta no visualizador; as abas sem foto e o aviso de fotos não extraídas.
 * Só o que a API traz (nunca valores pessoais).
 */

import { useState } from 'react';
import { DocViewer } from './DocViewer';
import { formatarDataVisita } from './format';
import { FotoRelatorio, RelatorioFotografico } from '../services/consulta-types';

const ROTULO_LIDA_DE: Readonly<Record<RelatorioFotografico['capa']['lidaDe'], string>> = {
  capa: 'a capa',
  nome_do_arquivo: 'o nome do arquivo (sem capa)',
  nenhum: '—',
};

const coordenada = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 });

/** O título de uma foto: a aba, o número e a legenda quando há. */
export function tituloDaFoto(f: FotoRelatorio): string {
  return `${f.aba} · Foto ${f.numero}${f.legenda ? ` — ${f.legenda}` : ''}`;
}

export function RelatorioFotograficoSecao({ rf }: { readonly rf: RelatorioFotografico }): JSX.Element {
  const [aberta, setAberta] = useState<number>(0);
  const foto = rf.fotos[aberta] ?? null;
  const c = rf.capa;
  return (
    <section className="card" aria-label="Relatório Fotográfico" data-testid="relatorio-fotografico">
      <h2>Relatório Fotográfico</h2>
      <p className="muted">
        <span className="mono">{rf.arquivo}</span>
        {rf.caminhoOriginal && <span className="mono">{` · ${rf.caminhoOriginal}`}</span>}
      </p>
      <table className="table">
        <tbody>
          <tr>
            <th>INEP lido de</th>
            <td>{ROTULO_LIDA_DE[c.lidaDe] ?? c.lidaDe}</td>
          </tr>
          <tr>
            <th>Data da instalação (capa)</th>
            <td>{c.data ? formatarDataVisita(c.data) : '—'}</td>
          </tr>
          <tr>
            <th>Coordenada (capa)</th>
            <td>{c.latitude !== null && c.longitude !== null ? `${coordenada.format(c.latitude)}, ${coordenada.format(c.longitude)}` : '—'}</td>
          </tr>
        </tbody>
      </table>
      {rf.fotosNaoExtraidas && (
        <p className="notice">Algumas fotos não puderam ser extraídas do arquivo — baixe o original para vê-las.</p>
      )}
      {rf.fotos.length === 0 ? (
        <p className="muted">Nenhuma foto extraída.</p>
      ) : (
        <div className="split">
          <ul className="lista-fotos" aria-label="Fotos do Relatório Fotográfico">
            {rf.fotos.map((f, i) => (
              <li key={`${f.aba}|${f.numero}`}>
                <button
                  type="button"
                  className={i === aberta ? 'btn btn-primary' : 'btn btn-ghost'}
                  aria-pressed={i === aberta}
                  onClick={() => setAberta(i)}
                >
                  {tituloDaFoto(f)}
                </button>
              </li>
            ))}
          </ul>
          <div>
            {foto && (
              <>
                <p className="muted mono">{foto.caminhoOriginal}</p>
                <DocViewer viewerUrl={foto.visualizadorUrl} />
              </>
            )}
          </div>
        </div>
      )}
      {rf.abasSemFoto.length > 0 && <p className="muted">{`Abas sem foto: ${rf.abasSemFoto.join(', ')}`}</p>}
    </section>
  );
}
